import React, { useState, useEffect } from "react";
import {
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  Button,
  Typography,
  IconButton,
  TextField,
  Alert,
} from "@mui/material";
import CloseIcon from "@mui/icons-material/Close";
import { useMutation, useQuery } from "@apollo/client";
import { UPDATE_EMPLOYEE_DETAILS } from "../../../../../../apis/organization-apis/people-apis/peopleApis";
import { GET_USER_DETAILS } from "../../../../../../apis/dashboard-apis/dashboardDetailApi";
import ConfirmSnackbar from "../../../../../../components/Snackbar/ConfirmSnackbar";
import { AccountCategoryKey } from "../../../../../../utils/enums";
import { buildScopeInput } from "../../utils/employeeUpdateInputs";
import { isSelfEmployee } from "../../utils/selfScopeGuard";

/**
 * Reusable bulk scope dialog for both directions. `mode`:
 *   "out" -> Mark selected Out of Scope (optional notes)
 *   "in"  -> Mark selected In Scope (employees with a termination date are skipped)
 * Direction is fixed by the menu item, so there's no radio toggle here.
 * Styling intentionally matches EditScopeDialog.
 */
const BulkScopeDialog = ({
  open,
  onClose,
  employees = [],
  mode = "out",
  onSaveSuccess,
  refetch,
}) => {
  const isInScope = mode === "in";
  const [updateEmployees, { loading: updating }] = useMutation(UPDATE_EMPLOYEE_DETAILS);
  const { data: userData } = useQuery(GET_USER_DETAILS);
  const currentUserName = userData?.getAccountDetails?.userDetails?.name || "";
  const currentEmployeeDataId =
    userData?.getAccountDetails?.userDetails?.employee_data_id;

  const [snackbarInfo, setSnackbarInfo] = useState(null);
  const [scopeReason, setScopeReason] = useState("");

  useEffect(() => {
    if (open) setScopeReason("");
  }, [open]);

  // When marking In Scope, employees with a termination date must use
  // "Clear Termination Date" instead — skip them here.
  // When marking Out of Scope, the signed-in user is skipped: doing it to
  // yourself revokes your own access with no way back from the UI.
  const eligible = isInScope
    ? employees.filter((e) => !e.termination_date)
    : employees.filter((e) => !isSelfEmployee(e, currentEmployeeDataId));
  const blockedByTermination = isInScope ? employees.length - eligible.length : 0;
  const selfSkipped = !isInScope && employees.length !== eligible.length;

  const handleSave = async () => {
    if (!employees.length) return;

    const input = eligible
      .map((emp) =>
        buildScopeInput(emp, {
          inScope: isInScope,
          accountCategory: AccountCategoryKey.NON_PERSON,
          scopeReason,
          currentUserName,
        })
      )
      .filter(Boolean);

    if (!input.length) {
      setSnackbarInfo({
        message: blockedByTermination
          ? "Selected employees have a termination date and can't be marked in scope."
          : selfSkipped
          ? "You can't mark yourself out of scope."
          : "None of the selected employees have a valid email to update",
        vertical: "top",
        horizontal: "right",
      });
      return;
    }

    try {
      const response = await updateEmployees({ variables: { input } });
      const result = response?.data?.updateEmployeeDetails;

      if (result?.success) {
        const message = `Marked ${isInScope ? "in scope" : "out of scope"} successfully.`;

        setSnackbarInfo({ message, vertical: "top", horizontal: "right" });
        onSaveSuccess?.();
        if (refetch) refetch();
        onClose();
      } else {
        setSnackbarInfo({
          message: result?.message || "Failed to update compliance scope",
          vertical: "top",
          horizontal: "right",
        });
      }
    } catch (error) {
      setSnackbarInfo({
        message: error?.message || "Failed to update compliance scope",
        vertical: "top",
        horizontal: "right",
      });
    }
  };

  return (
    <>
      <Dialog open={open} onClose={onClose} maxWidth="sm" fullWidth PaperProps={{ sx: { borderRadius: 2 } }}>
        <DialogTitle
          sx={{
            display: "flex",
            justifyContent: "space-between",
            alignItems: "center",
            pb: 1.5,
            pt: 2.5,
            px: 3,
            borderBottom: "1px solid #e5e7eb",
          }}
        >
          <Typography variant="h5" fontWeight={600} color="#111827">
            {isInScope ? "Mark In Scope" : "Mark Out of Scope"}
          </Typography>
          <IconButton onClick={onClose} size="small" sx={{ color: "#6b7280" }}>
            <CloseIcon />
          </IconButton>
        </DialogTitle>

        <DialogContent sx={{ px: 3, pb: 2, "&.MuiDialogContent-root": { pt: 2.5 } }}>
          <Alert severity="info" sx={{ mb: isInScope || selfSkipped ? 0 : 3 }}>
            {isInScope ? (
              <>
                <strong>{eligible.length}</strong> selected employee
                {eligible.length === 1 ? "" : "s"} will be included in compliance checks
                and employee workflows.
              </>
            ) : (
              <>
                <strong>{eligible.length}</strong> selected employee
                {eligible.length === 1 ? "" : "s"} will lose access to Regodit and be
                excluded from compliance checks and employee workflows.
              </>
            )}
          </Alert>

          {selfSkipped && (
            <Alert severity="warning" sx={{ mt: 2, mb: 0 }}>
              You're in this selection and will be skipped, since you can't mark
              yourself out of scope.
            </Alert>
          )}

          {isInScope && blockedByTermination > 0 && (
            <Alert severity="warning" sx={{ mt: 2, mb: 0 }}>
              <strong>{blockedByTermination}</strong> selected employee
              {blockedByTermination === 1 ? " has a termination date and will be skipped" : "s have a termination date and will be skipped"}.
              Clear the termination date first to reinstate them.
            </Alert>
          )}

          {!isInScope && (
            <TextField
              fullWidth
              label="Additional Notes (optional)"
              value={scopeReason}
              onChange={(e) => setScopeReason(e.target.value)}
              disabled={updating}
              multiline
              rows={3}
              sx={{ mt: selfSkipped ? 3 : 0, "& .MuiOutlinedInput-root": { borderRadius: "8px" } }}
              placeholder="Add any notes about why these employees are out of scope..."
            />
          )}
        </DialogContent>

        <DialogActions sx={{ px: 3, pb: 2.5, pt: 2, borderTop: "1px solid #e5e7eb" }}>
          <Button
            onClick={onClose}
            disabled={updating}
            sx={{ color: "#6b7280", fontWeight: 500, "&:hover": { backgroundColor: "#f3f4f6" } }}
          >
            Cancel
          </Button>
          <Button
            onClick={handleSave}
            variant="contained"
            disabled={updating || eligible.length === 0}
          >
            {updating ? "Saving..." : "Save"}
          </Button>
        </DialogActions>
      </Dialog>
      <ConfirmSnackbar snackbarInfo={snackbarInfo} />
    </>
  );
};

export default BulkScopeDialog;
